import { Injectable } from '@angular/core';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { TZDate } from '@date-fns/tz';
import { isValid } from 'date-fns';
import { TimeZoneSingleton } from 'app/core/services/prothesis-api/time-zone-singleton';
import { AppTzDate } from './app-tz-date';
import { parseFrenchDate } from './parse-french-date';

@Injectable()
export class LaboratoryDateFnsDateAdapter extends DateFnsAdapter {
  public override createDate(year: number, month: number, date: number): TZDate {
    if (month < 0 || month > 11) {
      throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
    }

    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }

    const timeZone = this._laboratoryTimeZone();
    const result = new AppTzDate(year, month, date, 0, 0, 0, 0, timeZone!);

    // Out-of-bounds days (e.g. Feb 30) roll over to the next month → reject.
    if (!isValid(result) || result.getMonth() !== month || result.getDate() !== date) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }

    return result;
  }

  public override deserialize(value: unknown): TZDate | null {
    if (value === null || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return this._midnightFromAbsolute(value.getTime());
    }

    if (typeof value === 'string') {
      const parsed = new Date(value);
      if (!isValid(parsed)) {
        return super.deserialize(value) as TZDate | null;
      }
      return this._midnightFromAbsolute(parsed.getTime());
    }

    return super.deserialize(value) as TZDate | null;
  }

  public override parse(value: unknown, parseFormat: string | string[]): TZDate | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const format = Array.isArray(parseFormat) ? parseFormat[0] : parseFormat;
      // Date-only parse in browser-local time; we only keep y/m/d, no timezone conversion.
      const parsed = parseFrenchDate(value, format);
      if (!parsed) {
        return null;
      }
      return new AppTzDate(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 0, 0, 0, 0, this._laboratoryTimeZone()!);
    }

    if (value instanceof Date) {
      return this._midnightFromAbsolute(value.getTime());
    }

    return null;
  }

  public override today(): TZDate {
    return this._midnightFromAbsolute(Date.now());
  }

  private _laboratoryTimeZone(): string | null {
    return TimeZoneSingleton.getInstance()?.laboratoryTimeZone;
  }

  // Converts an absolute instant to midnight (date-only) in the laboratory timezone.
  private _midnightFromAbsolute(millis: number): TZDate {
    const timeZone = this._laboratoryTimeZone();
    const inTz = new TZDate(millis, timeZone!);
    return new AppTzDate(inTz.getFullYear(), inTz.getMonth(), inTz.getDate(), 0, 0, 0, 0, timeZone!);
  }
}
